import { BackgroundJobStatus } from '@erp/shared-types';
import type { BackgroundJobDto } from '@erp/rpc';

import { rpcClient } from '@/api/rpc/client';

/**
 * Wait for a server-side background job to settle.
 *
 * The counterpart to the browser's own job panel: the work is running somewhere
 * else entirely, so all this tab can do is ask. Nothing here cancels anything —
 * if the tab closes mid-wait the server carries on, which is the point of moving
 * the work off the request in the first place.
 */

/** Slow enough to be cheap, quick enough that a short job still feels prompt. */
const POLL_INTERVAL_MS = 2000;

/**
 * How many polls in a row may fail before we stop watching.
 *
 * Counted consecutively rather than in total, and never as a wall-clock deadline:
 * extraction is allowed thirty minutes, so any overall timeout short enough to
 * catch a broken tab would also abandon work that is merely slow. A minute of
 * uninterrupted failure, on the other hand, is not a blip — it is a session that
 * expired or a server that is gone, and neither gets better by asking again.
 */
const MAX_CONSECUTIVE_FAILURES = 30;

export type BackgroundJobWaitResult =
  /** `job` is the terminal row, for callers that read results off its payload. */
  | { outcome: 'succeeded'; job: BackgroundJobDto }
  | { outcome: 'failed'; errorMessage: string | null }
  /**
   * The job is no longer readable — sweepable history, or a signed-in user who is
   * not its owner. Distinct from `failed` because we cannot say the work failed,
   * only that we have stopped being able to watch it.
   */
  | { outcome: 'vanished' };

/**
 * Poll `id` until it reaches a terminal state.
 *
 * Deliberately returns rather than throws for a failed job: a job failing is an
 * outcome the caller renders, not an exception. `onRunning` fires on the first
 * poll that sees the work actually started, so a caller can move its status line
 * on from "queued" without inventing a fraction — the server reports no
 * percentage for a model call that says nothing until it answers. Work that does
 * report one (a streamed conversion) reaches the caller through `onPoll`, which
 * fires with every reading of a still-running job.
 */
export async function waitForBackgroundJob(
  id: string,
  onRunning?: () => void,
  onPoll?: (job: BackgroundJobDto) => void,
): Promise<BackgroundJobWaitResult> {
  let announcedRunning = false;
  let consecutiveFailures = 0;

  for (;;) {
    try {
      const job = await rpcClient.core.backgroundJobs.get({ id });
      consecutiveFailures = 0;

      if (job === null) return { outcome: 'vanished' };

      if (!announcedRunning && job.status === BackgroundJobStatus.Running) {
        announcedRunning = true;
        onRunning?.();
      }
      if (job.status === BackgroundJobStatus.Running) {
        onPoll?.(job);
      }
      if (job.status === BackgroundJobStatus.Succeeded) {
        return { outcome: 'succeeded', job };
      }
      if (job.status === BackgroundJobStatus.Failed) {
        return { outcome: 'failed', errorMessage: job.errorMessage };
      }
    } catch {
      // A dropped poll says nothing about the job, which lives on the server and
      // will still be there next time. Keep waiting rather than reporting a
      // failure this tab merely failed to observe — but not forever. A logged-out
      // session answers every poll the same way, and retrying it until the tab
      // closes is just noise.
      consecutiveFailures += 1;
      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        return { outcome: 'vanished' };
      }
    }

    await new Promise((resolve) =>
      window.setTimeout(resolve, POLL_INTERVAL_MS),
    );
  }
}
